/**
 * TwoFactorService.js — Service wrapping the /auth/2fa/* and /auth/attach-email
 * endpoints (SPEC-2026-06-29-auth-confirm-2fa.v1).
 *
 * §5/§9 DRY — single import point for every 2FA / email-attach network call ;
 * mirrors the existing service pattern (AuthService.js) : every call goes through
 * the encrypted client `axiosWithToken` (base URL = REACT_APP_SERVER).
 *
 * Endpoints (see SPEC) :
 *   POST /auth/attach-email   { email }                 → sends verification code
 *   GET  /auth/2fa/status     → { totpEnabled, enrolledAt, layerEnabled, thresholdUsd }
 *   POST /auth/2fa/setup      → { secret, otpauthUri }  (only when not enabled)
 *   POST /auth/2fa/enable     { code }                  → { totpEnabled:true }
 *   POST /auth/2fa/disable    { code }                  → { totpEnabled:false }
 *   GET  /auth/2fa/admin/config            → { enabled, thresholdUsd }   (admin)
 *   PUT  /auth/2fa/admin/config { enabled?, thresholdUsd? } → updated config (admin)
 */
import { axiosWithToken } from '../utils/functions/apiCallWithToken';
import { SERVER_URL as serverURL } from '../core/config';


/* ── Email attach (verification gate) ──────────────────────────────────────── */
export const attachEmail = async (email) => {
    const result = await axiosWithToken(`${serverURL}/auth/attach-email`, 'post', { email });
    return result;
};

/* ── Pré-autorisation d'une SORTIE de fonds (retrait/claim > seuil USD) ───────────
 * Appelée AVANT le mouvement on-chain. Résout { ok:true } si non requis ou code valide ;
 * sinon l'Error porte err.code = 'TOTP_REQUIRED' (err.invalid si code fourni mais faux) ou
 * 'TOTP_ENROLL_REQUIRED' (modale globale d'enrôlement ouverte automatiquement par le client). */
export const preauthOutflow = async (amountUsd, code) => {
    const body = code ? { amountUsd, totpCode: code } : { amountUsd };
    const result = await axiosWithToken(`${serverURL}/auth/2fa/preauth`, 'post', body);
    return result;
};

/* ── User 2FA (TOTP) ───────────────────────────────────────────────────────── */
export const getStatus = async () => {
    // GET /auth/2fa/state (renommé depuis /status le 2026-06-30 — l'exclusion middleware AES-GCM
    // serveur sur '/status' rendait la réponse non-chiffrée et le client échouait au déchiffrement).
    const result = await axiosWithToken(`${serverURL}/auth/2fa/state`, 'get', {});
    return result;
};

export const setup = async () => {
    const result = await axiosWithToken(`${serverURL}/auth/2fa/setup`, 'post', {});
    return result;
};

export const enable = async (code) => {
    const result = await axiosWithToken(`${serverURL}/auth/2fa/enable`, 'post', { code });
    return result;
};

export const disable = async (code) => {
    const result = await axiosWithToken(`${serverURL}/auth/2fa/disable`, 'post', { code });
    return result;
};

/* ── Admin config ──────────────────────────────────────────────────────────── */
export const getAdminConfig = async () => {
    const result = await axiosWithToken(`${serverURL}/auth/2fa/admin/config`, 'get', {});
    return result;
};

export const updateAdminConfig = async (data) => {
    const result = await axiosWithToken(`${serverURL}/auth/2fa/admin/config`, 'put', data);
    return result;
};
